import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import StatusBadge from "@/components/dashboard/StatusBadge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["planned", "in_progress", "ready_for_review", "published", "needs_update"];
const APPROVAL = [
  { id: "draft", label: "Draft" },
  { id: "pending", label: "Pending review" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

export default function ContentItemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/content/detail/${id}`).then(({ data }) => setItem(data)).catch(() => navigate("/content-calendar"));
  }, [id, navigate]);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.patch(`/content/${id}`, patch);
      setItem((it) => ({ ...it, ...patch }));
      toast.success("Saved");
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    await save({ approval_status: "approved", status: "ready_for_review" });
    toast.success("Approved — ready for publish");
  };
  const reject = async () => {
    await save({ approval_status: "rejected", status: "needs_update" });
    toast.error("Marked as needs update");
  };

  if (!item) return <DashboardLayout><p className="text-muted-foreground">Loading…</p></DashboardLayout>;

  return (
    <DashboardLayout>
      <PageHeader
        overline={item.content_type?.replace(/_/g, " ") || "Content"}
        title={item.title}
        description={`Target keyword: ${item.target_keyword}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Select value={item.status} onValueChange={(v) => save({ status: v })}>
              <SelectTrigger className="w-44 h-9 rounded-sm bg-white" data-testid="detail-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}><StatusBadge status={s} /></SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={reject} disabled={saving} data-testid="detail-reject" className="px-4 py-2 border border-border hover:border-rose-400 hover:text-rose-600 rounded-sm text-sm">Request changes</button>
            <button onClick={approve} disabled={saving} data-testid="detail-approve" className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm disabled:opacity-50">
              Approve content
            </button>
          </div>
        }
      />

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-5">
          <Card title="SEO Brief" testId="seo-brief">
            <Field label="Angle" value={item.seo_brief?.angle} />
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field label="Search intent" value={item.seo_brief?.search_intent} />
              <Field label="Target word count" value={`${item.seo_brief?.target_word_count} words`} />
              <Field label="Funnel stage" value={item.funnel_stage} />
              <Field label="Estimated impact" value={item.estimated_impact} />
            </div>
            <div className="mt-5">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Secondary keywords</p>
              <div className="flex flex-wrap gap-2">
                {(item.seo_brief?.secondary_keywords || []).map((k) => (
                  <span key={k} className="px-2.5 py-1 text-xs bg-ivory-100 border border-border rounded-sm">{k}</span>
                ))}
              </div>
            </div>
            <div className="mt-5">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Entities to cover</p>
              <div className="flex flex-wrap gap-2">
                {(item.seo_brief?.entities || []).map((k) => (
                  <span key={k} className="px-2.5 py-1 text-xs bg-sage-400/10 border border-sage-400/40 rounded-sm">{k}</span>
                ))}
              </div>
            </div>
            <div className="mt-5">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Conversion hypothesis</p>
              <p className="text-sm mt-2 italic text-navy-900/80">{item.seo_brief?.conversion_hypothesis}</p>
            </div>
          </Card>

          <Card title="Article outline" testId="outline">
            <ol className="space-y-3">
              {(item.outline || []).map((s) => (
                <li key={s.heading} className="border-l-2 border-gold-500 pl-4">
                  <p className="font-serif text-lg">{s.heading}</p>
                  <p className="text-sm text-muted-foreground mt-1">{s.summary}</p>
                </li>
              ))}
            </ol>
            <div className="mt-5 flex gap-2">
              <Link to="/articles" className="text-xs px-4 py-2 bg-navy-900 hover:bg-navy-800 text-ivory-50 rounded-sm font-semibold" data-testid="open-article-gen">
                Generate full draft
              </Link>
            </div>
          </Card>

          <Card title="CTA block" testId="cta-block">
            <div className="border-l-4 border-gold-500 pl-5 py-2">
              <p className="font-serif text-2xl text-navy-900">{item.cta_block_headline}</p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{item.cta_block_body}</p>
              <p className="text-xs text-gold-600 uppercase tracking-widest mt-3">Primary CTA: {item.cta}</p>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-4 space-y-5">
          <Card title="Meta">
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Meta title</label>
                <Input value={item.meta_title || ""} onChange={(e) => setItem({ ...item, meta_title: e.target.value })} onBlur={() => save({ meta_title: item.meta_title })} className="mt-1.5 bg-white border-border rounded-sm" data-testid="meta-title-input" />
                <p className="text-[11px] text-muted-foreground mt-1">{(item.meta_title || "").length}/60</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Meta description</label>
                <Textarea rows={3} value={item.meta_description || ""} onChange={(e) => setItem({ ...item, meta_description: e.target.value })} onBlur={() => save({ meta_description: item.meta_description })} className="mt-1.5 bg-white border-border rounded-sm" data-testid="meta-description-input" />
                <p className="text-[11px] text-muted-foreground mt-1">{(item.meta_description || "").length}/155</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Slug</label>
                <Input value={item.slug || ""} onChange={(e) => setItem({ ...item, slug: e.target.value })} onBlur={() => save({ slug: item.slug })} className="mt-1.5 bg-white border-border rounded-sm font-mono text-xs" data-testid="slug-input" />
              </div>
            </div>
          </Card>

          <Card title="Internal links" testId="internal-links">
            <ul className="space-y-2">
              {(item.internal_links || []).map((l) => (
                <li key={l.url} className="text-sm border border-border rounded-sm p-3">
                  <p className="font-medium">{l.anchor}</p>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{l.url}</p>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Approval">
            <Select value={item.approval_status} onValueChange={(v) => save({ approval_status: v })}>
              <SelectTrigger className="rounded-sm bg-white" data-testid="approval-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {APPROVAL.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-3">Due {item.due_date}</p>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Card({ title, children, testId }) {
  return (
    <div className="bg-white border border-border rounded-sm p-6" data-testid={testId}>
      <p className="editorial-overline">{title}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
}
function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-sm text-navy-900 mt-1">{value || "—"}</p>
    </div>
  );
}
